import { createRoute, Outlet, redirect } from "@tanstack/react-router";
import { LoginScreen } from "@/app/Auth/Screens/LoginScreen";
import { SignUpScreen } from "@/app/Auth/Screens/SignUpScreen";
import { ForgotPasswordScreen } from "@/app/Auth/Screens/ForgotPasswordScreen";
import { OrganisationSelectScreen } from "@/app/Onboarding/Screens/OrganisationSelectScreen";
import { NoOrganisationLayout } from "@/app/Auth/Screens/NoOrganisationLayout";

import { rootRoute } from "@/routes/root-route";
import { AcceptInvitationScreen } from "@/app/Auth/Screens/AcceptInvitationScreen";
import { QueryClient } from "@/lib/ReactQuery/QueryClient";
import { organisationsQueryOptions } from "@/lib/Auth/organisationsQuery";
import { z } from "zod";

/**
 * Resolve the org membership count from real data — cached when warm, fetched on
 * a cold cache. The router context's `organisations` is populated by a
 * non-suspense query, so on a hard refresh it's still an empty array while the
 * query is in flight; reading it directly makes membership guards flash the
 * wrong screen (e.g. a multi-org user bounced to onboarding). Awaiting the query
 * here makes the guard decision authoritative.
 */
async function resolveOrganisationCount(token: string | null): Promise<number> {
  const data = await QueryClient.ensureQueryData(
    organisationsQueryOptions(token),
  );
  return data?.organisations?.length ?? 0;
}

export const noOrgLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_no_org",
  component: NoOrganisationLayout,
});

/**
 * Guest-only routes — redirect signed-in users away.
 */
export const signInRoute = createRoute({
  getParentRoute: () => noOrgLayoutRoute,
  path: "/sign-in",
  component: LoginScreen,
  /** Sign up hands the new account's address over so it lands on the code step. */
  validateSearch: (search: Record<string, unknown>): { email?: string } => ({
    email: typeof search.email === "string" ? search.email : undefined,
  }),
  beforeLoad: async ({ context }) => {
    if (context.auth.isSignedIn) {
      if ((await resolveOrganisationCount(context.auth.token)) === 0) {
        throw redirect({ to: "/onboarding" });
      }
      throw redirect({ to: "/" });
    }
  },
  head: () => ({
    meta: [{ title: "Sign In - Imoova Admin" }],
  }),
});

export const signUpRoute = createRoute({
  getParentRoute: () => noOrgLayoutRoute,
  path: "/sign-up",
  component: SignUpScreen,
  beforeLoad: async ({ context }) => {
    if (context.auth.isSignedIn) {
      if ((await resolveOrganisationCount(context.auth.token)) === 0) {
        throw redirect({ to: "/onboarding" });
      }
      throw redirect({ to: "/" });
    }
  },
  head: () => ({
    meta: [{ title: "Sign Up - Imoova Admin" }],
  }),
});

export const forgotPasswordRoute = createRoute({
  getParentRoute: () => noOrgLayoutRoute,
  path: "/forgot-password",
  component: ForgotPasswordScreen,
  beforeLoad: ({ context }) => {
    if (context.auth.isSignedIn) {
      throw redirect({ to: "/" });
    }
  },
  head: () => ({
    meta: [{ title: "Reset Password - Imoova Admin" }],
  }),
});

/**
 * Requires authentication.
 */
export const noOrgLoggedInLayout = createRoute({
  getParentRoute: () => noOrgLayoutRoute,
  id: "_logged_in",
  component: Outlet,
  beforeLoad: ({ context }) => {
    if (!context.auth.isSignedIn) {
      throw redirect({ to: "/sign-in" });
    }
  },
});

/**
 * Requires authentication + at least one organisation.
 */
export const hasOrgsLayout = createRoute({
  getParentRoute: () => noOrgLoggedInLayout,
  id: "_has_orgs",
  component: Outlet,
  beforeLoad: async ({ context }) => {
    if ((await resolveOrganisationCount(context.auth.token)) === 0) {
      throw redirect({ to: "/onboarding" });
    }
  },
});

/**
 * Requires authentication + no organisations (onboarding).
 */
export const noOrgsLayout = createRoute({
  getParentRoute: () => noOrgLoggedInLayout,
  id: "_no_orgs",
  component: Outlet,
  beforeLoad: async ({ context }) => {
    if ((await resolveOrganisationCount(context.auth.token)) > 0) {
      throw redirect({ to: "/org" });
    }
  },
});

export const changeOrganisationRoute = createRoute({
  getParentRoute: () => hasOrgsLayout,
  path: "/org",
  component: OrganisationSelectScreen,
  head: () => ({
    meta: [{ title: "Change Organisation - Imoova Admin" }],
  }),
});

/** Back-compat: old `/change-organisation` bookmarks → `/org`. */
export const changeOrganisationRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/change-organisation",
  beforeLoad: () => {
    throw redirect({ to: "/org" });
  },
});

const invitationValidation = z.object({
  token: z.string().optional(),
});

export const acceptInvitationRoute = createRoute({
  validateSearch: invitationValidation,
  getParentRoute: () => noOrgLayoutRoute,
  path: "/accept-invitation",
  component: AcceptInvitationScreen,
});
